import type { Bi } from 'schools/tantra/types/bilingual';

export type TantraTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type TantraChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: TantraTopic[];
};

export const TANTRA_HERO = {
  eyebrow: { en: 'Inner science · Mantra · Ritual wisdom', hi: 'अंतर विज्ञान · मंत्र · कर्म ज्ञान' },
  titleShort: { en: 'Tantra', hi: 'तंत्र' },
  title: { en: 'Tantra', hi: 'तंत्र' },
  motto: {
    en: 'Understand Tantra as philosophy, scripture, and method - with respect for guru lineage and ethical study.',
    hi: 'तंत्र को दर्शन, शास्त्र और विधि के रूप में समझें - गुरु परंपरा और नैतिक अध्ययन के साथ।',
  },
  tagline: {
    en: 'Thirty-two bilingual lessons across four modules - traditions, practice vocabulary, cross-links with Yoga and Jyotisha, and responsible study boundaries.',
    hi: 'चार मॉड्यूल में बत्तीस द्विभाषी पाठ - परंपराएँ, अभ्यास शब्दावली, योग और ज्योतिष से जुड़ाव, और जिम्मेदार अध्ययन सीमाएँ।',
  },
  intro: {
    en: 'Pair with Yantra Vidya for sacred geometry, Swara Vigyan for breath channels, and Panchang for ritual timing.',
    hi: 'पवित्र ज्यामिति हेतु यंत्र विद्या, श्वास नाड़ी हेतु स्वर विज्ञान, और कर्म समय हेतु पंचांग के साथ पढ़ें।',
  },
  trait: {
    en: 'Śiva + Śakti + mantra + ethical guidance = responsible study',
    hi: 'शिव + शक्ति + मंत्र + नैतिक मार्गदर्शन = जिम्मेदार अध्ययन',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): TantraTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const TANTRA_CHAPTERS: TantraChapter[] = [
  {
    id: 'foundations',
    number: 1,
    title: { en: 'Foundations & history', hi: 'नींव और इतिहास' },
    subtitle: { en: 'What Tantra is and how it evolved', hi: 'तंत्र क्या है और कैसे विकसित हुआ' },
    icon: '🔥',
    topics: [
      topic('what-is-tantra', 'What Is Tantra?', 'तंत्र क्या है?', 'Beyond stereotypes - method, scripture, and worldview.', 'रूढ़ियों से परे - विधि, शास्त्र और दृष्टि।', 12, '🔥'),
      topic('tantra-etymology-scope', 'Tantra: Text, Method, Tradition', 'तंत्र: ग्रंथ, विधि, परंपरा', 'Three senses of the word tantra in plain language.', 'तंत्र शब्द के तीन अर्थ सरल भाषा में।', 10, '📜'),
      topic('history-pre-classical', 'Pre-classical Roots', 'पूर्व-शास्त्रीय मूल', 'From Vedic ritual culture toward Āgamic turn.', 'वैदिक कर्म संस्कृति से आगमिक मोड़ की ओर।', 12, '🏛️'),
      topic('history-medieval-expansion', 'Medieval Expansion', 'मध्यकालीन विस्तार', 'Shaiva, Shakta, and Buddhist Tantra flourish.', 'शैव, शाक्त और बौद्ध तंत्र का उत्थान।', 13, '🗺️'),
      topic('history-modern-reception', 'Modern Reception', 'आधुनिक ग्रहण', 'Scholarship, reform, and popular misconceptions.', 'शोध, सुधार और लोकप्रिय गलतफहमियाँ।', 11, '📰'),
      topic('shiva-shakti-principle', 'Shiva–Shakti', 'शिव–शक्ति', 'Consciousness and energy as complementary principles.', 'चेतना और ऊर्जा पूरक सिद्धांत।', 12, '⚡'),
      topic('advaita-tantra', 'Non-duality in Tantric Thought', 'तांत्रिक अद्वैत', 'Recognition and unity in Kashmir Shaiva frame.', 'कश्मीर शैव दृष्टि में प्रत्यभिज्ञा और एकता।', 13, '☯'),
      topic('tantra-vs-puranic-vs-vedic', 'Tantra, Purāṇa & Vedic Streams', 'तंत्र, पुराण और वैदिक धाराएँ', 'Parallel streams that interpenetrate - not rivals.', 'मिलती-जुलती धाराएँ - प्रतिद्वंद्वी नहीं।', 12, '🔀'),
    ],
  },
  {
    id: 'traditions-scriptures',
    number: 2,
    title: { en: 'Traditions & scriptures', hi: 'परंपराएँ और शास्त्र' },
    subtitle: { en: 'Major lineages and textual sources', hi: 'प्रमुख संप्रदाय और ग्रंथ स्रोत' },
    icon: '📚',
    topics: [
      topic('shaiva-tantra-overview', 'Shaiva Tantra', 'शैव तंत्र', 'Kashmir, Siddhānta, and temple Śaiva streams.', 'कश्मीर, सिद्धांत और मंदिर शैव धाराएँ।', 13, '🔱'),
      topic('shakta-tantra-overview', 'Śākta Tantra', 'शाक्त तंत्र', 'Goddess-centred Kaula and Śrī Vidyā paths.', 'देवी-केंद्रित कौल और श्री विद्या मार्ग।', 13, '🌺'),
      topic('vaishnava-tantra-overview', 'Vaiṣṇava Tantra', 'वैष्णव तंत्र', 'Pañcarātra and temple Viṣṇu worship.', 'पाञ्चरात्र और मंदिर विष्णु उपासना।', 11, '🪷'),
      topic('buddhist-tantra-overview', 'Buddhist Tantra', 'बौद्ध तंत्र', 'Vajrayāna, deity yoga, and emptiness.', 'वज्रयान, देवता योग और शून्यता।', 13, '🕉'),
      topic('agamas-vs-tantras', 'Āgamas vs Tantras', 'आगम बनाम तंत्र', 'Scriptural families and authority structures.', 'शास्त्र परिवार और प्रामाणिकता संरचना।', 12, '📖'),
      topic('reading-tantric-texts', 'How to Read Tantric Texts', 'तांत्रिक ग्रंथ कैसे पढ़ें', 'Symbol, secrecy, and commentarial tradition.', 'प्रतीक, गोपनीयता और टीका परंपरा।', 12, '🔍'),
      topic('major-texts-shaiva', 'Key Shaiva Texts', 'प्रमुख शैव ग्रंथ', 'Orientation to core Śaiva Tantric sources.', 'मुख्य शैव तांत्रिक स्रोतों का परिचय।', 12, '📜'),
      topic('major-texts-shakta', 'Key Śākta Texts', 'प्रमुख शाक्त ग्रंथ', 'Devī corpus and Śrī Vidyā literature.', 'देवी साहित्य और श्री विद्या ग्रंथ।', 12, '📿'),
    ],
  },
  {
    id: 'practice-vocabulary',
    number: 3,
    title: { en: 'Practice vocabulary', hi: 'अभ्यास शब्दावली' },
    subtitle: { en: 'Mantra, yantra, mudrā, nyāsa, and subtle body', hi: 'मंत्र, यंत्र, मुद्रा, न्यास और सूक्ष्म शरीर' },
    icon: '🙏',
    topics: [
      topic('mantra-in-tantra', 'Mantra in Tantra', 'तंत्र में मंत्र', 'Sound, deity, and intention - study, not assignment.', 'ध्वनि, देवता और संकल्प - अध्ययन, आरोपण नहीं।', 12, '🔊'),
      topic('yantra-in-tantra', 'Yantra in Tantric Sādhana', 'तांत्रिक साधना में यंत्र', 'Form in worship - learn geometry in Yantra Vidya.', 'उपासना में रूप - ज्यामिति यंत्र विद्या में सीखें।', 11, '✡'),
      topic('mudra-in-tantra', 'Mudrā', 'मुद्रा', 'Gesture as embodied sign and inner state.', 'मुद्रा शारीरिक संकेत और अंतःस्थिति।', 11, '🤲'),
      topic('nyasa-in-tantra', 'Nyāsa', 'न्यास', 'Placing divinity in the body - conceptual map only.', 'शरीर में दिव्यता का स्थान - केवल संकल्पना मानचित्र।', 12, '🧍'),
      topic('chakra-system-overview', 'Chakra Map', 'चक्र मानचित्र', 'Seven centres in Tantric physiology.', 'तांत्रिक शरीर विज्ञान में सात केंद्र।', 12, '⭕'),
      topic('kundalini-concept', 'Kuṇḍalinī', 'कुण्डलिनी', 'Coiled energy metaphor - traditional and scholarly.', 'कुण्डलित ऊर्जा रूपक - पारंपरिक और शोधपूर्ण।', 13, '🐍'),
      topic('nadis-prana', 'Nāḍīs & Prāṇa', 'नाड़ी और प्राण', 'Channels and life-force in Tantric maps.', 'तांत्रिक मानचित्रों में नाड़ी और प्राण।', 12, '〰️'),
      topic('puja-architecture', 'Pūjā Structure', 'पूजा संरचना', 'Ritual grammar from āvāhana to visarjana.', 'आवाहन से विसर्जन तक कर्म व्याकरण।', 12, '🪔'),
    ],
  },
  {
    id: 'integration-ethics',
    number: 4,
    title: { en: 'Integration & ethics', hi: 'एकीकरण और नैतिकता' },
    subtitle: { en: 'Cross-school links, lineage, and responsible study', hi: 'अन्य स्कूल से जुड़ाव, परंपरा और जिम्मेदार अध्ययन' },
    icon: '🌐',
    topics: [
      topic('temple-ritual-agamic', 'Temple Ritual & Āgamic Worship', 'मंदिर कर्म और आगमिक पूजा', 'Spatial mandala and priestly worship.', 'स्थानिक मंडल और पुरोहित कर्म।', 12, '🛕'),
      topic('tantra-and-jyotisha', 'Tantra ↔ Jyotiṣa', 'तंत्र ↔ ज्योतिष', 'Timing, nakṣatra, and occult-house themes.', 'समय, नक्षत्र और रहस्य भाव विषय।', 12, '♃'),
      topic('tantra-and-ayurveda', 'Tantra ↔ Āyurveda', 'तंत्र ↔ आयुर्वेद', 'Prāṇa, marma, and rasāyana connections.', 'प्राण, मर्म और रसायन जुड़ाव।', 11, '🌿'),
      topic('tantra-and-yoga', 'Tantra ↔ Yoga', 'तंत्र ↔ योग', 'Aṣṭāṅga, haṭha, and kuṇḍalinī overlap.', 'अष्टांग, हठ और कुण्डलिनी का मिलन।', 12, '🧘'),
      topic('tantra-and-vastu', 'Tantra ↔ Vāstu', 'तंत्र ↔ वास्तु', 'Mandala on plot vs yantra on altar.', 'प्लॉट पर मंडल बनाम वेदी पर यंत्र।', 11, '🧭'),
      topic('influential-teachers', 'Lineages & Teachers', 'परंपरा और आचार्य', 'Abhinavagupta, Lakṣmaṇadeśika, and key masters.', 'अभिनवगुप्त, लक्ष्मणदेशिक और प्रमुख आचार्य।', 12, '👤'),
      topic('diksha-and-guru', 'Dīkṣā & Guru', 'दीक्षा और गुरु', 'Initiation, sampradāya, and qualified guidance.', 'दीक्षा, संप्रदाय और योग्य मार्गदर्शन।', 13, '🙏'),
      topic('ethics-responsible-study', 'Ethics & Responsible Study', 'नैतिकता और जिम्मेदार अध्ययन', 'Study boundaries - no fear-selling or misuse.', 'अध्ययन सीमाएँ - भय बिक्री या दुरुपयोग नहीं।', 12, '⚖️'),
    ],
  },
];

export const TANTRA_TOPIC_IDS = TANTRA_CHAPTERS.flatMap((c) => c.topics.map((t) => t.id));

export function getTantraTopicCount(): number {
  return TANTRA_TOPIC_IDS.length;
}

export function getTantraChapter(chapterId: string): TantraChapter | null {
  return TANTRA_CHAPTERS.find((c) => c.id === chapterId) ?? null;
}

export function getTantraChapterIndex(chapterId: string): number {
  return TANTRA_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getTantraTopic(topicId: string): { topic: TantraTopic; chapter: TantraChapter } | null {
  for (const chapter of TANTRA_CHAPTERS) {
    const topicMeta = chapter.topics.find((t) => t.id === topicId);
    if (topicMeta) return { topic: topicMeta, chapter };
  }
  return null;
}

export function getTantraTopicNeighbors(topicId: string): {
  prev: TantraTopic | null;
  next: TantraTopic | null;
} {
  const flat = TANTRA_CHAPTERS.flatMap((c) => c.topics);
  const idx = flat.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flat[idx - 1] : null,
    next: idx < flat.length - 1 ? flat[idx + 1] : null,
  };
}
